// DS: C109
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { ResourceType } from '../../types/resource';
import DeviationDuperbrowseContext from '../../Duperbrowse/DeviationDuperbrowseContext';
import { DeviationTypes } from '../../types/deviation';
import { dateToDistanceTrans } from '@wix/da-ds/pkg/Time/functions';
import { getDeviationType } from '../../DeviationViews/_helpers';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import UserAvatar from '../../User/Avatar/UserAvatar';
import UserLinkWithTooltip from '../../User/Link/UserLinkWithTooltip';
import Flex from '@wix/da-ds/pkg/Flex';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Slider from '@wix/da-ds/pkg/Slider';
import UserWatchGhostButton from '../UserWatchGhostButton';
import { isGroup as getIsGroup } from '../../User/helpers';
import s from './UserSampleGallery.scss';
const DESKTOP_THUMB_DIMS = {
    width: 163,
    height: 110
};
const MOBILE_THUMB_DIMS = {
    width: 117,
    height: 78
};
const BI_SECTIONNAME = 'watch_suggestions';
/** Gruser blurb, watch button, and small row of their deviations */ export const UserSampleGallery = ({ user, isOwnUser, dataHook, deviations, duperbrowseContextTitle, onWatch, className })=>{
    const { profile, stats, details, useridUuid, userId } = user;
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    // fire a bi event on the mobile gallery when scrolled, but only once
    const biData = useMemo(()=>({
            typeid: ResourceType.USER,
            itemid: userId
        }), [
        userId
    ]);
    // build a limited gallery of deviations wrapped in duperbrowse
    const thumbs = deviations && /*#__PURE__*/ React.createElement(DeviationDuperbrowseContext, {
        streamId: `UserSampleGallery-${useridUuid}`,
        deviations: deviations,
        parent: {
            title: duperbrowseContextTitle
        }
    }, deviations.map((deviation, idx)=>/*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
            key: `thumb-${idx}`,
            value: {
                rank: idx
            }
        }, /*#__PURE__*/ React.createElement(DeviationLink, {
            deviation: deviation,
            className: s['thumb-link']
        }, /*#__PURE__*/ React.createElement(DeviationDumbView, _object_spread({
            deviation: deviation
        }, isMobile ? MOBILE_THUMB_DIMS : DESKTOP_THUMB_DIMS, getDeviationType(deviation) === DeviationTypes.LITERATURE && {
            className: s['literature-thumb'],
            coverLetterClassName: s['literature-thumb-cover-letter']
        }))))));
    const isGroup = getIsGroup(user);
    const tagline = (profile === null || profile === void 0 ? void 0 : profile.tagline) || '';
    const allow2LineName = !tagline && !isMobile;
    const joinedDistance = details && dateToDistanceTrans(details.joindate);
    const joinedDistanceLocaleKey = isMobile ? 'watchSuggestionsV2.userSampleGallery.joinedDateDistance.mobile' : 'watchSuggestionsV2.userSampleGallery.joinedDateDistance';
    return /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: BI_SECTIONNAME
        }
    }, /*#__PURE__*/ React.createElement(Section, {
        className: classnames(s['root'], isGroup && s['is-group'], className),
        "data-hook": dataHook
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['content']
    }, /*#__PURE__*/ React.createElement(UserAvatar, {
        user: user,
        size: isMobile ? 32 : 50,
        className: classnames(s['avatar'], user.usericonDefault && s['avatar-default'])
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['user-info']
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: s['user-link-heading']
    }, /*#__PURE__*/ React.createElement(UserLinkWithTooltip, {
        user: user,
        withTooltip: true,
        className: allow2LineName ? s['user-link-2-line'] : s['user-link']
    })), tagline && /*#__PURE__*/ React.createElement("span", {
        className: s['tagline']
    }, tagline), /*#__PURE__*/ React.createElement("div", {
        className: s['stats']
    }, stats && /*#__PURE__*/ React.createElement("span", {
        className: s['stat-item']
    }, shortenNumber(stats.watchers, 0), ' ', t('watchSuggestionsV2.userSampleGallery.watchers', {
        formattedCount: stats.watchers,
        count: stats.watchers
    })), joinedDistance && /*#__PURE__*/ React.createElement("span", {
        className: s['stat-item']
    }, t(joinedDistanceLocaleKey, {
        distance: t(joinedDistance.key, {
            count: joinedDistance.value
        })
    })))), /*#__PURE__*/ React.createElement(Flex, {
        className: s['watch-join-buttons']
    }, !isOwnUser && /*#__PURE__*/ React.createElement(UserWatchGhostButton, {
        author: user,
        className: s['watch-button'],
        onWatch: onWatch
    }), isGroup && /*#__PURE__*/ React.createElement(GhostButton, {
        className: s['join-button'],
        size: "small",
        variant: "approval-green",
        href: Url.userLink(user.username),
        "aria-hidden": true
    }, t('common.join')))), isMobile ? /*#__PURE__*/ React.createElement(Slider, {
        className: s['gallery'],
        slimSpacing: true,
        onScrollBiData: biData
    }, thumbs) : /*#__PURE__*/ React.createElement(Flex, {
        className: s['gallery']
    }, thumbs)));
};
UserSampleGallery.displayName = 'UserSampleGallery';
export default UserSampleGallery;
